'use client';
/**
 * Add / Reduce / Remove handlers + local input state for HoldingsList.
 * Co-located so the orchestrator stays render-focused. Each handler
 * resolves to { ok, error? } so the UI can surface failures inline
 * without throwing.
 */
import { useState } from 'react';
import type { ScoreRow } from '@/lib/hooks/use-holdings-data';

interface UseHoldingsRowActionsArgs {
  scores: Map<string, ScoreRow> | null;
  portfolio: { symbols: string[] } | null;
  commitAddPosition: (sym: string, shares: number, price: number) => Promise<{ ok: boolean; error?: string }>;
  commitReducePosition: (sym: string, shares: number, sellPx?: number) => Promise<{ ok: boolean; error?: string }>;
  commitRemovePosition: (sym: string) => Promise<{ ok: boolean; error?: string }>;
  removePosition: (sym: string) => void;
  indexEtfs: Set<string>;
}

export function useHoldingsRowActions({
  scores,
  portfolio,
  commitAddPosition,
  commitReducePosition,
  commitRemovePosition,
  removePosition,
  indexEtfs,
}: UseHoldingsRowActionsArgs) {
  // "+ add position" input bar (visible when editing). Shares + buy price (the
  // way you'd describe a real trade) rather than raw weight%. Conversion:
  // weight = shares × price / bookNav. Buy price is persisted as
  // entryPrices[sym] for tax-lot tracking.
  const [addSym, setAddSym] = useState('');
  const [addPrice, setAddPrice] = useState('');
  const [addShares, setAddShares] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  // "− reduce position" input bar. Shares to sell + (optional) sell price.
  // Sell price is informational for now — used later for realized-P&L logging.
  const [reduceSym, setReduceSym] = useState('');
  const [reduceShares, setReduceShares] = useState('');
  const [reducePrice, setReducePrice] = useState('');
  const [reduceError, setReduceError] = useState<string | null>(null);

  async function handleReducePosition() {
    const sym = reduceSym.trim().toUpperCase();
    const sh = parseFloat(reduceShares);
    const sellPx = parseFloat(reducePrice);
    if (!sym) { setReduceError('enter a ticker'); return; }
    if (!portfolio?.symbols.includes(sym)) { setReduceError(`${sym} not in portfolio`); return; }
    if (!Number.isFinite(sh) || sh <= 0) {
      setReduceError('enter share count');
      return;
    }
    setReduceError(null);
    const result = await commitReducePosition(sym, sh, Number.isFinite(sellPx) && sellPx > 0 ? sellPx : undefined);
    if (!result.ok) {
      setReduceError(result.error ?? 'reduce failed');
      return;
    }
    setReduceSym('');
    setReduceShares('');
    setReducePrice('');
  }

  async function handleAddPosition() {
    const sym = addSym.trim().toUpperCase();
    const price = parseFloat(addPrice);
    const shares = parseFloat(addShares);
    if (!sym) { setAddError('enter a ticker'); return; }
    if (!scores?.has(sym) && !indexEtfs.has(sym)) {
      setAddError(`${sym} not in universe`);
      return;
    }
    if (!Number.isFinite(price) || price <= 0) { setAddError('enter buy price'); return; }
    if (!Number.isFinite(shares) || shares <= 0) { setAddError('enter share count'); return; }
    setAddError(null);
    // Auto-persist + reload. After reload the table re-renders with the new
    // ticker and every dependent panel (visualizer 3D scatter, sector pie,
    // headline count, "MY PORTFOLIO N" badge) reflects the change.
    const result = await commitAddPosition(sym, shares, price);
    if (!result.ok) {
      setAddError(result.error ?? 'save failed');
      return;
    }
    setAddSym('');
    setAddPrice('');
    setAddShares('');
  }

  async function handleRemoveRow(symbol: string) {
    setAddError(null);
    // Auto-persist + reload. Existing soft-remove (used inside edit-weights
    // mode) is still wired via removePosition for the cancel-friendly batch
    // path, but the legend-driven flow uses immediate persist.
    const result = await commitRemovePosition(symbol);
    if (!result.ok) {
      // Fall back to soft-remove + show the error inline so the row still
      // disappears visually but the save error is surfaced.
      removePosition(symbol);
      setAddError(result.error ?? 'remove failed');
    }
  }

  return {
    // add state + setter pairs
    addSym, setAddSym,
    addPrice, setAddPrice,
    addShares, setAddShares,
    addError, setAddError,
    // reduce state + setter pairs
    reduceSym, setReduceSym,
    reduceShares, setReduceShares,
    reducePrice, setReducePrice,
    reduceError, setReduceError,
    // actions
    handleAddPosition,
    handleReducePosition,
    handleRemoveRow,
  };
}
